/**
 * The earn codes that may ONLY be entered on an employee's locked non-billable
 * default job panel (#30083), plus their user-facing labels.
 *
 * Pure TypeScript, no framework and no DTO imports, so web and NativeScript
 * share one definition of the rule. `libs/xplat/utils` is tagged
 * `type:util` + `framework:agnostic`, which bars a dependency on
 * `@ups/xplat/api/dto` (`type:lib` + `framework:angular`) at lint time.
 */

/**
 * Vacation, sick and holiday are the non-worked codes, and the NB default
 * job/phase is the single line they are allowed on.
 *
 * This list is the SINGLE SOURCE OF TRUTH for that rule. It exists because the
 * rule was previously copy-pasted into each column's `[hidden]` expression in
 * the web template, and holiday was simply left out of two of the three, so
 * holiday was enterable on every job panel while vacation and sick were locked.
 * Adding a code here locks it everywhere at once, on every platform.
 */
export const NON_BILLABLE_ONLY_EARN_CODES = ['vac', 'sick', 'hol'] as const;

export type NonBillableOnlyEarnCode =
  (typeof NON_BILLABLE_ONLY_EARN_CODES)[number];

export type EarnCodeField = 'st' | 'ot' | 'pt' | 'trv' | 'vac' | 'sick' | 'hol';

/**
 * User-facing labels for {@link NON_BILLABLE_ONLY_EARN_CODES}. Keyed as a
 * `Record` over that union so adding a 4th locked code is a compile error until
 * its label exists (banner copy had drifted to "vacation and sick" after HOL
 * joined the lock).
 */
export const NON_BILLABLE_ONLY_EARN_CODE_LABELS: Record<
  NonBillableOnlyEarnCode,
  string
> = {
  vac: 'vacation',
  sick: 'sick',
  hol: 'holiday',
};

/** Comma-and-"and" list for banners, e.g. "vacation, sick and holiday". */
export function formatNonBillableOnlyEarnCodeLabels(): string {
  const labels = NON_BILLABLE_ONLY_EARN_CODES.map(
    (code) => NON_BILLABLE_ONLY_EARN_CODE_LABELS[code]
  );
  if (labels.length <= 1) return labels[0] ?? '';
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

/** True when `field` may only be entered on the locked non-billable default panel. */
export function isNonBillableOnlyEarnCode(field: EarnCodeField): boolean {
  return (NON_BILLABLE_ONLY_EARN_CODES as readonly string[]).includes(field);
}
